import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { 
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction 
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ArrowLeft, FileSearch, Wand2, Loader2, CheckCircle, AlertCircle, Check,
  Image, Layers, FolderOpen, Mic, Film, RefreshCw, Upload, AlertTriangle,
  Filter, Trash2, FileText, Download, FolderInput, X, Plus, Link2, Search
} from "lucide-react";
import { uploadFileToS3 } from "@/utils/s3Upload";

import { getApiUrl, getApiBaseUrl } from '@/config/api';

const API = getApiUrl();

const getAssetUrl = (fileUrl) => {
  if (!fileUrl) return '';
  const backendUrl = getApiBaseUrl();
  
  // Handle old absolute URLs - extract the path and use current backend
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    const apiMatch = fileUrl.match(/\/api\/(files|exports)\/(.+)/);
    if (apiMatch) {
      return `${backendUrl}/api/${apiMatch[1]}/${apiMatch[2]}`;
    }
    return fileUrl;
  }
  
  if (fileUrl.startsWith('/api/')) {
    return `${backendUrl}${fileUrl}`;
  }
  return `${backendUrl}/api/${fileUrl.replace(/^\//, '')}`;
};

const ASSET_TYPE_CONFIG = {
  background: { label: "Sets", icon: Image, color: "amber", accept: "image/png,image/jpeg,image/webp" },
  character: { label: "Characters", icon: Layers, color: "indigo", accept: "image/png,image/jpeg,image/webp" },
  prop: { label: "Items", icon: FolderOpen, color: "purple", accept: "image/png,image/jpeg,image/webp" },
};

export default function ScriptTools() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { getAuthHeaders } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [creatingScenes, setCreatingScenes] = useState(false);
  const [deletingBlank, setDeletingBlank] = useState(false);
  const [inventory, setInventory] = useState(null);
  const [autoSceneResult, setAutoSceneResult] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);
  const [existingScenesCount, setExistingScenesCount] = useState(0); // Track existing scenes
  
  // Scene creation progress state
  const [sceneProgress, setSceneProgress] = useState(null); // { current, total, sceneName, estimatedSeconds }
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState("all"); // all, missing, found
  const [typeFilters, setTypeFilters] = useState(["background", "character", "prop"]); // multi-select: Sets, Characters, Items
  
  // Excluded items (manually deleted from inventory view)
  const [excludedItems, setExcludedItems] = useState(new Set());
  
  // Upload state
  const [uploadingFor, setUploadingFor] = useState(null); // {name, type}
  const [uploading, setUploading] = useState(false);
  
  // Warning dialog state
  const [showMissingWarning, setShowMissingWarning] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingScenes, setDeletingScenes] = useState(false);
  
  // PDF generation state
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState("");
  const [scriptType, setScriptType] = useState("full");
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Import from project state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importingFor, setImportingFor] = useState(null); // {name, type}
  const [importableProjects, setImportableProjects] = useState([]);
  const [selectedImportProject, setSelectedImportProject] = useState("");
  const [importableAssets, setImportableAssets] = useState([]);
  const [selectedImportAssets, setSelectedImportAssets] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Asset detail modal state
  const [showAssetDetailModal, setShowAssetDetailModal] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [projectAssets, setProjectAssets] = useState([]);
  const [characterProfiles, setCharacterProfiles] = useState([]);
  
  // Manual linking state
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkSearchQuery, setLinkSearchQuery] = useState("");
  const [linkingItem, setLinkingItem] = useState(null);
  
  // Loading saved inventory state
  const [loadingInventory, setLoadingInventory] = useState(true);

  useEffect(() => {
    fetchProject();
    fetchSavedInventory();
    fetchProjectAssets();
    fetchCharacterProfiles();
    fetchScenesCount();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      setProject(response.data);
    } catch (error) {
      toast.error("Failed to load project");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchScenesCount = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/scenes`, {
        headers: getAuthHeaders()
      });
      setExistingScenesCount(response.data?.length || 0);
    } catch (error) {
      // Silent failure
    }
  };

  const fetchProjectAssets = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/assets`, {
        headers: getAuthHeaders()
      });
      setProjectAssets(response.data || []);
    } catch (error) {
      // Silent failure - assets will just be empty
    }
  };

  const fetchCharacterProfiles = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/characters`, {
        headers: getAuthHeaders()
      });
      setCharacterProfiles(response.data || []);
    } catch (error) {
      // Silent failure
    }
  };

  const fetchSavedInventory = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/art-inventory`, {
        headers: getAuthHeaders()
      });
      if (response.data.inventory) {
        // Transform sets to backgrounds for backward compatibility in UI
        const inv = response.data.inventory;
        setInventory({
          characters: inv.characters || [],
          backgrounds: inv.sets || [], // Map 'sets' to 'backgrounds' for UI
          props: inv.props || [],
          analyzed_at: inv.analyzed_at
        });
      }
    } catch (error) {
      // Expected when no inventory saved yet
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleAnalyzeScript = async () => {
    if (!project?.script?.trim()) {
      toast.error("No script found. Please add a script first.");
      return;
    }

    setAnalyzing(true);
    setInventory(null);

    try {
      // Start the art inventory analysis job
      const startResponse = await axios.post(`${API}/projects/${projectId}/art-inventory/analyze`, {}, {
        headers: getAuthHeaders()
      });
      
      const jobId = startResponse.data.job_id;
      
      if (!jobId) {
        toast.error("Failed to start analysis");
        setAnalyzing(false);
        return;
      }
      
      toast.info("Analyzing script for art inventory... This may take a moment.");
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 60; // 60 attempts * 2 seconds = 2 minutes max
      
      const pollForResult = async () => {
        while (attempts < maxAttempts) {
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds between polls
          
          try {
            const statusResponse = await axios.get(
              `${API}/projects/${projectId}/art-inventory/status/${jobId}`,
              { headers: getAuthHeaders() }
            );
            
            const status = statusResponse.data.status;
            
            if (status === "complete") {
              const result = statusResponse.data.result;
              // Transform sets to backgrounds for UI compatibility
              setInventory({
                characters: result.characters || [],
                backgrounds: result.sets || [],
                props: result.props || [],
                analyzed_at: result.analyzed_at
              });
              toast.success("Art inventory analysis complete!");
              return;
            } else if (status === "error") {
              toast.error(statusResponse.data.error || "Analysis failed");
              return;
            }
            // If still processing, continue polling
          } catch (pollError) {
            // If 404, job might have been cleaned up - check for saved inventory
            if (pollError.response?.status === 404) {
              const savedInventory = await axios.get(
                `${API}/projects/${projectId}/art-inventory`,
                { headers: getAuthHeaders() }
              );
              if (savedInventory.data?.inventory) {
                const inv = savedInventory.data.inventory;
                setInventory({
                  characters: inv.characters || [],
                  backgrounds: inv.sets || [],
                  props: inv.props || [],
                  analyzed_at: inv.analyzed_at
                });
                toast.success("Art inventory analysis complete!");
                return;
              }
            }
            console.error("Polling error:", pollError);
          }
        }
        
        toast.error("Analysis timed out. Please try again.");
      };
      
      await pollForResult();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze script");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAutoCreateScenes = async (force = false) => {
    if (!project?.script?.trim()) {
      toast.error("No script found. Please add a script first.");
      return;
    }

    // Check for missing assets if inventory exists and not forcing
    if (!force && inventory) {
      const missingCount = getMissingCount();
      if (missingCount > 0) {
        setShowMissingWarning(true);
        return;
      }
    }

    setCreatingScenes(true);
    setAutoSceneResult(null);
    setShowMissingWarning(false);
    setSceneProgress(null);

    try {
      // Start background job (or resume if interrupted)
      const startResponse = await axios.post(
        `${API}/projects/${projectId}/auto-scenes-job`,
        {},
        { headers: getAuthHeaders() }
      );
      
      const { job_id, status, total_scenes, scenes_processed, message } = startResponse.data;
      
      // Handle already completed case
      if (status === "already_completed") {
        setExistingScenesCount(scenes_processed);
        toast.info(message);
        setCreatingScenes(false);
        return;
      }
      
      // Show initial progress
      if (status === "resuming") {
        toast.info(`Resuming from shot ${scenes_processed + 1} of ${total_scenes}`);
      } else {
        toast.info(`Creating ${total_scenes} shots...`);
      }
      
      setSceneProgress({
        current: scenes_processed,
        total: total_scenes,
        sceneName: '',
        estimatedSeconds: startResponse.data.estimated_seconds
      });
      
      // Poll for progress
      let completed = false;
      let pollCount = 0;
      const maxPolls = 1000; // Safety limit (~16 minutes at 1 second intervals)
      
      while (!completed && pollCount < maxPolls) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Poll every second
        pollCount++;
        
        try {
          const statusResponse = await axios.get(
            `${API}/projects/${projectId}/auto-scenes-job/status`,
            { headers: getAuthHeaders() }
          );
          
          const { status: jobStatus, scenes_processed: current, total_scenes: total, current_scene_name, error, can_resume } = statusResponse.data;
          
          // Update progress
          setSceneProgress({
            current,
            total,
            sceneName: current_scene_name,
            status: jobStatus === 'processing' ? 'generating' : 'created'
          });
          
          if (jobStatus === "completed") {
            completed = true;
            setAutoSceneResult({ scenes_created: current });
            setExistingScenesCount(current);
            toast.success(`Created ${current} shots!`);
          } else if (jobStatus === "failed") {
            completed = true;
            toast.error(error || "Failed to create shots");
          } else if (jobStatus === "interrupted" && can_resume) {
            // Job was interrupted (server restart, timeout, etc.) - auto-resume
            toast.info(`Connection interrupted at shot ${current}. Resuming...`);
            
            // Resume the job
            await axios.post(
              `${API}/projects/${projectId}/auto-scenes-job/resume`,
              {},
              { headers: getAuthHeaders() }
            );
            
            // Continue polling
          }
        } catch (pollError) {
          // Network error during polling - wait and retry
          console.warn("Polling error, retrying...", pollError);
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      if (!completed && pollCount >= maxPolls) {
        toast.warning("Taking longer than expected. Check back in a few minutes.");
      }
      
    } catch (error) {
      console.error("Scene creation error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to create scenes");
    } finally {
      setCreatingScenes(false);
      setSceneProgress(null);
    }
  };

  const getMissingCount = () => {
    if (!inventory) return 0;
    const missingChars = inventory.characters?.filter(c => !c.exists).length || 0;
    const missingBgs = inventory.backgrounds?.filter(b => !b.exists).length || 0;
    const missingProps = inventory.props?.filter(p => !p.exists).length || 0;
    return missingChars + missingBgs + missingProps;
  };

  const handleDeleteAllScenes = async () => {
    setDeletingScenes(true);
    setDeleteResult(null);
    setShowDeleteConfirm(false);

    try {
      const response = await axios.delete(`${API}/projects/${projectId}/all-scenes`, {
        headers: getAuthHeaders()
      });
      setDeleteResult(response.data);
      if (response.data.deleted_count > 0) {
        toast.success(`Deleted ${response.data.deleted_count} shots`);
        setExistingScenesCount(0);
        setAutoSceneResult(null);
      } else {
        toast.info("No shots found");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete shots");
    } finally {
      setDeletingScenes(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!selectedCharacter) {
      toast.error("Please select a character");
      return;
    }

    setGeneratingPdf(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/generate-script-pdf`, {
        character_name: selectedCharacter,
        script_type: scriptType
      }, {
        headers: getAuthHeaders()
      });
      
      // Open PDF in new tab (convert URL using getAssetUrl)
      window.open(getAssetUrl(response.data.pdf_url), "_blank");
      toast.success(`Generated ${scriptType === "full" ? "full script" : "sides"} for ${selectedCharacter}`);
      setShowPdfDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  const getCharactersList = () => {
    if (!inventory?.characters) return [];
    return inventory.characters.map(c => c.name);
  };

  // Get assets matching an inventory item - uses match_id from backend when available
  const getMatchingAssets = (itemName, itemType, matchId = null, matchType = null) => {
    // If we have a match_id from the backend, use it directly
    if (matchId) {
      if (itemType === "character" && matchType === "profile") {
        // Find the character profile by ID
        const profile = characterProfiles.find(p => p.id === matchId);
        return profile ? [profile] : [];
      } else if (itemType === "character" && matchType === "asset") {
        // Find the asset by ID
        const asset = projectAssets.find(a => a.id === matchId);
        return asset ? [asset] : [];
      } else {
        // For backgrounds/props, find the asset by ID
        const asset = projectAssets.find(a => a.id === matchId);
        return asset ? [asset] : [];
      }
    }
    
    // Fallback to name-based matching
    if (!itemName) return [];
    const nameLower = itemName.toLowerCase().trim();
    
    // For characters, match against character profiles (not assets)
    if (itemType === "character") {
      if (!characterProfiles || characterProfiles.length === 0) return [];
      return characterProfiles.filter(profile => {
        const profileNameLower = (profile.name || "").toLowerCase().trim();
        // Exact match or close match (name contains the full profile name or vice versa)
        return profileNameLower === nameLower || 
               profileNameLower === nameLower.replace(/[^a-z0-9]/g, '') ||
               nameLower === profileNameLower.replace(/[^a-z0-9]/g, '');
      });
    }
    
    // For backgrounds and props, match against project assets
    if (!projectAssets || projectAssets.length === 0) return [];
    
    // Map inventory types to asset types
    const assetTypeMap = {
      "background": "background",
      "prop": "prop",
      "set": "background"  // Sets map to backgrounds
    };
    const targetAssetType = assetTypeMap[itemType] || itemType;
    
    return projectAssets.filter(asset => {
      if (asset.type !== targetAssetType) return false;
      const assetNameLower = (asset.name || "").toLowerCase().trim();
      // Stricter matching - require substantial overlap
      return assetNameLower === nameLower || 
             assetNameLower.includes(nameLower) || 
             nameLower.includes(assetNameLower);
    });
  };

  // Handle clicking on an inventory item to open detail modal
  const handleInventoryItemClick = (item) => {
    setSelectedInventoryItem(item);
    setShowAssetDetailModal(true);
  };

  // Handle manual linking of inventory item to profile/asset
  const handleManualLink = async (targetId, targetName, targetType) => {
    if (!linkingItem) return;
    
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/art-inventory/link`,
        {
          item_name: linkingItem.name,
          item_type: linkingItem.type,
          target_id: targetId,
          target_name: targetName,
          target_type: targetType
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Update local inventory state
        if (inventory) {
          const typeMap = { character: "characters", set: "backgrounds", background: "backgrounds", prop: "props" };
          const key = typeMap[linkingItem.type] || linkingItem.type + "s";
          const updatedInventory = { ...inventory };
          const items = updatedInventory[key] || [];
          const itemIndex = items.findIndex(i => i.name === linkingItem.name);
          if (itemIndex >= 0) {
            items[itemIndex] = {
              ...items[itemIndex],
              exists: !!targetId,
              asset_id: targetId,
              match_id: targetId,
              match_name: targetName,
              match_type: targetType,
              manual_link: !!targetId
            };
            setInventory(updatedInventory);
          }
        }
        
        // Update selected item if modal is open
        if (selectedInventoryItem && selectedInventoryItem.name === linkingItem.name) {
          setSelectedInventoryItem({
            ...selectedInventoryItem,
            exists: !!targetId,
            asset_id: targetId,
            match_name: targetName,
            match_type: targetType
          });
        }
        
        setShowLinkDialog(false);
        setLinkingItem(null);
        setLinkSearchQuery("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link item");
    }
  };

  // Open link dialog for an inventory item
  const openLinkDialog = (item) => {
    setLinkingItem(item);
    setLinkSearchQuery("");
    setShowLinkDialog(true);
  };

  // Get available targets for linking based on item type
  const getLinkTargets = () => {
    if (!linkingItem) return [];
    
    const query = linkSearchQuery.toLowerCase();
    
    if (linkingItem.type === "character") {
      // Show character profiles
      return characterProfiles
        .filter(p => !query || p.name?.toLowerCase().includes(query))
        .map(p => ({
          id: p.id,
          name: p.name,
          type: "profile",
          imageCount: p.reference_images?.length || 0,
          thumbnail: p.reference_images?.[0]?.file_url
        }));
    } else if (linkingItem.type === "background" || linkingItem.type === "set") {
      // Show background assets
      return projectAssets
        .filter(a => a.type === "background")
        .filter(a => !query || a.name?.toLowerCase().includes(query))
        .map(a => ({
          id: a.id,
          name: a.name,
          type: "asset",
          thumbnail: a.file_url
        }));
    } else {
      // Show prop assets
      return projectAssets
        .filter(a => a.type === "prop")
        .filter(a => !query || a.name?.toLowerCase().includes(query))
        .map(a => ({
          id: a.id,
          name: a.name,
          type: "asset",
          thumbnail: a.file_url
        }));
    }
  };

  // Remove an asset from the project
  const handleRemoveAsset = async (assetId) => {
    try {
      await axios.delete(`${API}/assets/${assetId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Asset removed");
      fetchProjectAssets();
      fetchCharacterProfiles();
      // Refresh inventory to update counts
      handleAnalyzeScript();
    } catch (error) {
      toast.error("Failed to remove asset");
    }
  };

  // Import from project functions
  const handleImportClick = async (name, type) => {
    setImportingFor({ name, type });
    setSelectedImportProject("");
    setImportableAssets([]);
    setSelectedImportAssets([]);
    setShowImportDialog(true);
    
    // Fetch importable projects
    setLoadingProjects(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/importable-projects`, {
        headers: getAuthHeaders()
      });
      setImportableProjects(response.data);
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectSelect = async (sourceProjectId) => {
    setSelectedImportProject(sourceProjectId);
    setSelectedImportAssets([]);
    
    if (!sourceProjectId || !importingFor) return;
    
    setLoadingAssets(true);
    try {
      const response = await axios.get(`${API}/projects/${sourceProjectId}/exportable-assets`, {
        headers: getAuthHeaders()
      });
      // Filter assets by the type we're looking for and fuzzy match the name
      const matchingAssets = response.data.filter(asset => {
        if (asset.type !== importingFor.type) return false;
        // Fuzzy match - check if names are similar
        const assetNameLower = asset.name.toLowerCase();
        const searchNameLower = importingFor.name.toLowerCase();
        return assetNameLower.includes(searchNameLower) || 
               searchNameLower.includes(assetNameLower) ||
               assetNameLower.split(' ').some(word => searchNameLower.includes(word)) ||
               searchNameLower.split(' ').some(word => assetNameLower.includes(word));
      });
      
      // Also include all assets of the same type for manual selection
      const allTypeAssets = response.data.filter(asset => asset.type === importingFor.type);
      
      setImportableAssets(allTypeAssets);
      // Pre-select matching assets
      setSelectedImportAssets(matchingAssets.map(a => a.id));
    } catch (error) {
      toast.error("Failed to load assets");
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleImportAssets = async () => {
    if (selectedImportAssets.length === 0) {
      toast.error("Please select at least one asset to import");
      return;
    }
    
    setImporting(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/import-assets`, {
        source_project_id: selectedImportProject,
        asset_ids: selectedImportAssets
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success(`Imported ${response.data.imported_count} asset(s)`);
      setShowImportDialog(false);
      // Refresh inventory
      handleAnalyzeScript();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import assets");
    } finally {
      setImporting(false);
    }
  };

  const toggleImportAsset = (assetId) => {
    setSelectedImportAssets(prev => {
      if (prev.includes(assetId)) {
        return prev.filter(id => id !== assetId);
      } else {
        return [...prev, assetId];
      }
    });
  };

  const handleUploadClick = (name, type) => {
    setUploadingFor({ name, type });
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length || !uploadingFor) return;

    setUploading(true);
    
    // Support multiple file uploads using direct S3 upload
    for (const file of files) {
      try {
        await uploadFileToS3(file, projectId, {
          type: uploadingFor.type,
          name: uploadingFor.name
        });
        toast.success(`Uploaded ${file.name} for ${uploadingFor.name}`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
    setUploadingFor(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    
    // Refresh assets, character profiles, and inventory
    fetchProjectAssets();
    fetchCharacterProfiles();
    handleAnalyzeScript();
  };

  const toggleTypeFilter = (type) => {
    setTypeFilters(prev => {
      if (prev.includes(type)) {
        // Don't allow removing all filters
        if (prev.length === 1) return prev;
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleRemoveFromInventory = (itemName, itemType) => {
    const key = `${itemType}:${itemName.toLowerCase()}`;
    setExcludedItems(prev => new Set([...prev, key]));
    toast.success(`Removed "${itemName}" from inventory`);
  };

  // Filter and combine all inventory items
  const getFilteredInventory = () => {
    if (!inventory) return [];
    
    let items = [];
    
    // Helper to check if item is excluded
    const isExcluded = (name, type) => excludedItems.has(`${type}:${name.toLowerCase()}`);
    
    if (typeFilters.includes("character")) {
      items = [...items, ...(inventory.characters || [])
        .filter(c => !isExcluded(c.name || c, "character"))
        .map(c => ({ 
          name: c.name || c, 
          exists: c.exists, 
          asset_id: c.asset_id || c.match_id, 
          match_name: c.match_name,
          match_type: c.match_type,
          has_speaking_lines: c.has_speaking_lines || c.has_dialogue,
          context: c.context || c.description,
          type: "character" 
        }))];
    }
    if (typeFilters.includes("background")) {
      items = [...items, ...(inventory.backgrounds || [])
        .filter(b => !isExcluded(b.name || b, "background"))
        .map(b => ({ 
          name: b.name || b,
          exists: b.exists,
          asset_id: b.asset_id || b.match_id,
          match_name: b.match_name,
          context: b.context || b.description,
          type: "background" 
        }))];
    }
    if (typeFilters.includes("prop")) {
      items = [...items, ...(inventory.props || [])
        .filter(p => !isExcluded(p.name || p, "prop"))
        .map(p => ({ 
          name: p.name || p,
          exists: p.exists,
          asset_id: p.asset_id || p.match_id,
          match_name: p.match_name,
          context: p.context || p.description,
          type: "prop" 
        }))];
    }
    
    // Apply status filter
    if (statusFilter === "missing") {
      items = items.filter(item => !item.exists);
    } else if (statusFilter === "found") {
      items = items.filter(item => item.exists);
    }
    
    return items;
  };

  const getAssetIcon = (type) => {
    const config = ASSET_TYPE_CONFIG[type];
    if (!config) return <Film className="w-4 h-4" />;
    const Icon = config.icon;
    return <Icon className="w-4 h-4" />;
  };

  const getTypeColor = (type) => {
    const colors = {
      character: "text-indigo-400",
      background: "text-amber-400",
      prop: "text-purple-400"
    };
    return colors[type] || "text-zinc-400";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const filteredInventory = getFilteredInventory();
  const totalItems = (inventory?.characters?.length || 0) + (inventory?.backgrounds?.length || 0) + (inventory?.props?.length || 0);
  const foundItems = (inventory?.characters?.filter(c => c.exists).length || 0) + 
                     (inventory?.backgrounds?.filter(b => b.exists).length || 0) + 
                     (inventory?.props?.filter(p => p.exists).length || 0);

  return (
    <div className="min-h-screen bg-[#09090b] animate-fadeIn">
      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={uploadingFor ? ASSET_TYPE_CONFIG[uploadingFor.type]?.accept : "image/*"}
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Missing Assets Warning Dialog */}
      <Dialog open={showMissingWarning} onOpenChange={setShowMissingWarning}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Missing Assets Warning
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300 mb-4">
              There are <span className="text-amber-400 font-semibold">{getMissingCount()} missing assets</span> in your inventory. 
              Scenes created without these assets won&apos;t have characters or backgrounds automatically assigned.
            </p>
            <p className="text-zinc-500 text-sm">
              You can continue anyway, or go back and upload the missing assets first.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="ghost" className="text-zinc-400">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={() => handleAutoCreateScenes(true)}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              Continue Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Scenes Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete All Scenes
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300 mb-4">
              This will permanently delete <span className="text-red-400 font-semibold">all scenes</span> from this project.
            </p>
            <p className="text-zinc-500 text-sm">
              This action cannot be undone. You may want to keep your assets and script intact to recreate scenes later.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="ghost" className="text-zinc-400">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleDeleteAllScenes}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete All Scenes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Generation Dialog */}
      <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-500" />
              Generate Script PDF
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Select Character</Label>
              <Select value={selectedCharacter} onValueChange={setSelectedCharacter}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Choose a character..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {getCharactersList().map((char) => (
                    <SelectItem key={char} value={char} className="text-white hover:bg-zinc-700">
                      {char}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {getCharactersList().length === 0 && (
                <p className="text-amber-400 text-xs">Run Asset Inventory to detect characters first</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Script Type</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={scriptType === "full" ? "default" : "outline"}
                  onClick={() => setScriptType("full")}
                  className={scriptType === "full" 
                    ? "bg-green-500 hover:bg-green-600 text-black" 
                    : "border-zinc-700 text-zinc-300"
                  }
                >
                  Full Script
                </Button>
                <Button
                  variant={scriptType === "sides" ? "default" : "outline"}
                  onClick={() => setScriptType("sides")}
                  className={scriptType === "sides" 
                    ? "bg-green-500 hover:bg-green-600 text-black" 
                    : "border-zinc-700 text-zinc-300"
                  }
                >
                  Sides Only
                </Button>
              </div>
              <p className="text-zinc-500 text-xs">
                {scriptType === "full" 
                  ? "Full script with character's lines highlighted in yellow"
                  : "Only the character's lines and surrounding dialogue/cues"
                }
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="ghost" className="text-zinc-400">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleGeneratePdf}
              disabled={generatingPdf || !selectedCharacter}
              className="bg-green-500 hover:bg-green-600 text-black gap-2"
            >
              {generatingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Generate & Download
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from Project Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FolderInput className="w-5 h-5 text-blue-500" />
              Import {importingFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Project Selection */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Select Source Project</Label>
              {loadingProjects ? (
                <div className="flex items-center gap-2 text-zinc-500 p-3">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading projects...
                </div>
              ) : (
                <Select value={selectedImportProject} onValueChange={handleProjectSelect}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Choose a project..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {importableProjects.map((proj) => (
                      <SelectItem key={proj.id} value={proj.id} className="text-white hover:bg-zinc-700">
                        {proj.name} ({proj.asset_count} assets)
                      </SelectItem>
                    ))}
                    {importableProjects.length === 0 && (
                      <div className="p-2 text-zinc-500 text-sm">No other projects found</div>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Asset Selection */}
            {selectedImportProject && (
              <div className="space-y-2">
                <Label className="text-zinc-300">
                  Select {ASSET_TYPE_CONFIG[importingFor?.type]?.label || "Assets"} to Import
                </Label>
                {loadingAssets ? (
                  <div className="flex items-center gap-2 text-zinc-500 p-3">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading assets...
                  </div>
                ) : importableAssets.length === 0 ? (
                  <div className="p-3 bg-zinc-800/50 rounded-lg text-zinc-500 text-sm">
                    No {importingFor?.type}s found in this project
                  </div>
                ) : (
                  <ScrollArea className="h-[200px] border border-zinc-800 rounded-lg">
                    <div className="p-2 space-y-1">
                      {importableAssets.map((asset) => (
                        <div 
                          key={asset.id}
                          className="flex items-center gap-3 p-2 hover:bg-zinc-800/50 rounded cursor-pointer"
                          onClick={() => toggleImportAsset(asset.id)}
                        >
                          <Checkbox 
                            checked={selectedImportAssets.includes(asset.id)}
                            onCheckedChange={() => toggleImportAsset(asset.id)}
                            className="border-zinc-600"
                          />
                          <span className={getTypeColor(asset.type)}>
                            {getAssetIcon(asset.type)}
                          </span>
                          <span className="text-white text-sm flex-1 truncate">{asset.name}</span>
                          {asset.name.toLowerCase().includes(importingFor?.name?.toLowerCase() || "") && (
                            <Badge className="bg-blue-500/20 text-blue-400 border-0 text-[10px]">
                              Match
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
                {selectedImportAssets.length > 0 && (
                  <p className="text-xs text-zinc-500">
                    {selectedImportAssets.length} asset(s) selected
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="ghost" className="text-zinc-400">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleImportAssets}
              disabled={importing || selectedImportAssets.length === 0}
              className="bg-blue-500 hover:bg-blue-600 gap-2"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <FolderInput className="w-4 h-4" />
                  Import Selected
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asset Detail Modal */}
      <Dialog open={showAssetDetailModal} onOpenChange={setShowAssetDetailModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedInventoryItem && (
                <>
                  <span className={getTypeColor(selectedInventoryItem.type)}>
                    {getAssetIcon(selectedInventoryItem.type)}
                  </span>
                  {selectedInventoryItem.name}
                  <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500 ml-2">
                    {ASSET_TYPE_CONFIG[selectedInventoryItem.type]?.label.slice(0, -1)}
                  </Badge>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedInventoryItem && (
            <div className="py-4 space-y-4">
              {/* Context info */}
              {selectedInventoryItem.context && (
                <p className="text-sm text-zinc-400 italic bg-zinc-800/50 p-3 rounded-lg">
                  &ldquo;{selectedInventoryItem.context}&rdquo;
                </p>
              )}
              
              {/* Match info with link/change option */}
              <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center gap-2">
                  {selectedInventoryItem.exists && selectedInventoryItem.match_name ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-green-400">
                        Matched to: <strong>{selectedInventoryItem.match_name}</strong>
                        {selectedInventoryItem.manual_link && (
                          <span className="ml-1 text-xs text-zinc-500">(manual)</span>
                        )}
                      </span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <span className="text-sm text-red-400">Not linked to any profile</span>
                    </>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openLinkDialog(selectedInventoryItem)}
                  className="h-7 px-2 text-xs border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  data-testid="change-link-btn"
                >
                  <Link2 className="w-3 h-3 mr-1" />
                  {selectedInventoryItem.exists ? "Change Link" : "Link to Profile"}
                </Button>
              </div>
              
              {/* Attached Assets */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300">
                    Attached Files ({(() => {
                      const items = getMatchingAssets(selectedInventoryItem.name, selectedInventoryItem.type, selectedInventoryItem.asset_id, selectedInventoryItem.match_type);
                      if (selectedInventoryItem.type === "character" && items.length > 0) {
                        // For characters, count reference_images
                        return items[0].reference_images?.length || 0;
                      }
                      return items.length;
                    })()})
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setUploadingFor({ name: selectedInventoryItem.name, type: selectedInventoryItem.type });
                        fileInputRef.current?.click();
                      }}
                      disabled={uploading}
                      className="h-7 px-2 text-xs border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      Upload
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowAssetDetailModal(false);
                        handleImportClick(selectedInventoryItem.name, selectedInventoryItem.type);
                      }}
                      className="h-7 px-2 text-xs border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    >
                      <FolderInput className="w-3 h-3 mr-1" />
                      Import
                    </Button>
                  </div>
                </div>
                
                {(() => {
                  const items = getMatchingAssets(selectedInventoryItem.name, selectedInventoryItem.type, selectedInventoryItem.asset_id, selectedInventoryItem.match_type);
                  if (selectedInventoryItem.type === "character" && items.length > 0) {
                    return (items[0].reference_images?.length || 0) === 0;
                  }
                  return items.length === 0;
                })() ? (
                  <div className="p-8 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700 text-center">
                    <AlertCircle className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                    <p className="text-zinc-500 text-sm">No files attached yet</p>
                    <p className="text-zinc-600 text-xs mt-1">Upload or import files to attach them to this item</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[300px] overflow-y-auto p-1">
                    {(() => {
                      const matchedItems = getMatchingAssets(selectedInventoryItem.name, selectedInventoryItem.type, selectedInventoryItem.asset_id, selectedInventoryItem.match_type);
                      
                      // For character profiles, flatten reference_images into displayable items
                      if (selectedInventoryItem.type === "character" && matchedItems.length > 0) {
                        const profile = matchedItems[0];
                        const images = profile.reference_images || [];
                        
                        if (images.length === 0) {
                          return (
                            <div className="col-span-full p-4 text-center text-zinc-500">
                              <p>Profile found but no images uploaded yet</p>
                            </div>
                          );
                        }
                        
                        return images.map((img, idx) => (
                          <div 
                            key={img.asset_id || idx}
                            className="group relative bg-zinc-800/50 rounded-lg overflow-hidden border border-zinc-700 hover:border-zinc-600 transition-colors"
                          >
                            <div className="aspect-square bg-zinc-900 flex items-center justify-center">
                              {img.file_url ? (
                                <img 
                                  src={getAssetUrl(img.file_url)} 
                                  alt={`${profile.name} - Image ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-indigo-400">
                                  <Layers className="w-8 h-8" />
                                </span>
                              )}
                            </div>
                            <div className="p-2">
                              <p className="text-xs text-white truncate">{img.label || `Reference ${idx + 1}`}</p>
                            </div>
                          </div>
                        ));
                      }
                      
                      // For backgrounds/sets and props - show assets directly
                      return matchedItems.map((asset) => (
                        <div 
                          key={asset.id}
                          className="group relative bg-zinc-800/50 rounded-lg overflow-hidden border border-zinc-700 hover:border-zinc-600 transition-colors"
                        >
                          <div className="aspect-square bg-zinc-900 flex items-center justify-center">
                            {asset.file_url ? (
                              <img 
                                src={getAssetUrl(asset.file_url)} 
                                alt={asset.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className={getTypeColor(asset.type)}>
                                {getAssetIcon(asset.type)}
                              </span>
                            )}
                          </div>
                          <div className="p-2">
                            <p className="text-xs text-white truncate" title={asset.name}>{asset.name}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveAsset(asset.id)}
                            className="absolute top-1 right-1 w-6 h-6 p-0 bg-black/50 text-zinc-400 hover:text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ));
                    })()}
                    
                    {/* Add more button */}
                    <div 
                      className="aspect-square bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700 hover:border-zinc-500 flex flex-col items-center justify-center cursor-pointer transition-colors"
                      onClick={() => {
                        setUploadingFor({ name: selectedInventoryItem.name, type: selectedInventoryItem.type });
                        fileInputRef.current?.click();
                      }}
                    >
                      <Plus className="w-6 h-6 text-zinc-600 mb-1" />
                      <span className="text-xs text-zinc-600">Add more</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" className="text-zinc-400">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-500" />
              {linkingItem?.exists ? "Change Link" : "Link to Profile"}
            </DialogTitle>
          </DialogHeader>
          
          {linkingItem && (
            <div className="py-4 space-y-4">
              <p className="text-sm text-zinc-400">
                Select a {linkingItem.type === "character" ? "character profile" : "asset"} to link 
                <span className="font-semibold text-white"> &ldquo;{linkingItem.name}&rdquo;</span> to:
              </p>
              
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  placeholder={`Search ${linkingItem.type === "character" ? "character profiles" : "assets"}...`}
                  value={linkSearchQuery}
                  onChange={(e) => setLinkSearchQuery(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
              </div>
              
              {/* Available Targets */}
              <ScrollArea className="h-[300px] border border-zinc-800 rounded-lg">
                <div className="p-2 space-y-1">
                  {/* Unlink option */}
                  {linkingItem.exists && (
                    <button
                      onClick={() => handleManualLink(null, null, null)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-left transition-colors mb-2"
                    >
                      <X className="w-5 h-5 text-red-400" />
                      <span className="text-red-400 font-medium">Remove Link</span>
                    </button>
                  )}
                  
                  {getLinkTargets().map((target) => (
                    <button
                      key={target.id}
                      onClick={() => handleManualLink(target.id, target.name, target.type)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 border transition-colors text-left ${
                        linkingItem.asset_id === target.id 
                          ? "border-green-500/50 bg-green-500/10" 
                          : "border-transparent hover:border-blue-500/50"
                      }`}
                    >
                      {/* Thumbnail */}
                      <div className="w-12 h-12 rounded bg-zinc-700 overflow-hidden shrink-0">
                        {target.thumbnail ? (
                          <img 
                            src={getAssetUrl(target.thumbnail)} 
                            alt="" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {linkingItem.type === "character" ? (
                              <Layers className="w-5 h-5 text-zinc-600" />
                            ) : (
                              <Image className="w-5 h-5 text-zinc-600" />
                            )}
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-medium truncate">{target.name}</div>
                        <div className="text-xs text-zinc-500">
                          {target.type === "profile" 
                            ? `${target.imageCount || 0} images` 
                            : "Asset"
                          }
                        </div>
                      </div>
                      {/* Current selection indicator */}
                      {linkingItem.asset_id === target.id && (
                        <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                      )}
                    </button>
                  ))}
                  
                  {getLinkTargets().length === 0 && (
                    <div className="text-center text-zinc-500 text-sm py-8">
                      No {linkingItem.type === "character" ? "character profiles" : "assets"} found
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => { setShowLinkDialog(false); setLinkingItem(null); }} 
              className="text-zinc-400"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/project/${projectId}`)}
              className="text-zinc-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-white">Script Tools</h1>
              <p className="text-sm text-zinc-400">{project?.name}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Tool Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Inventory Tool */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <FileSearch className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Asset Inventory</CardTitle>
                  <CardDescription className="text-zinc-400">
                    Scan script for characters, backgrounds & props
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-zinc-500 mb-4">
                Analyzes your script to identify all characters, locations (backgrounds), 
                and props mentioned. Shows which assets exist in your library and which are missing.
              </p>
              <Button 
                onClick={handleAnalyzeScript}
                disabled={analyzing}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black gap-2"
                data-testid="analyze-script-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <FileSearch className="w-4 h-4" />
                    Generate Inventory
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Auto Scene Tool */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-indigo-500/10">
                  <Wand2 className="w-6 h-6 text-indigo-500" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Create Shot List</CardTitle>
                  <CardDescription className="text-zinc-400">
                    Automatically create a shot list from your script
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-zinc-500 mb-4">
                Analyzes your script and creates a shot list for you to compose your animations. Shots are grouped into Scenes automatically.
              </p>
              
              {/* Existing shots indicator */}
              {existingScenesCount > 0 && (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-green-300">
                      {existingScenesCount} shot{existingScenesCount !== 1 ? 's' : ''} created
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    View your shots in the Shot List or Storyboard
                  </p>
                </div>
              )}
              
              {/* Progress indicator */}
              {sceneProgress && (
                <div className="mb-4 p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-indigo-300">
                      Processing shot {sceneProgress.current} of {sceneProgress.total}
                    </span>
                    <span className="text-xs text-indigo-400">
                      {Math.round((sceneProgress.current / sceneProgress.total) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-2 mb-2">
                    <div 
                      className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(sceneProgress.current / sceneProgress.total) * 100}%` }}
                    />
                  </div>
                  {sceneProgress.sceneName && (
                    <p className="text-xs text-zinc-400 truncate">
                      {sceneProgress.status === 'generating' ? 'Generating direction for: ' : 'Created: '}
                      {sceneProgress.sceneName}
                    </p>
                  )}
                  {sceneProgress.estimatedSeconds && sceneProgress.current === 0 && (
                    <p className="text-xs text-zinc-500 mt-1">
                      Estimated time: ~{Math.ceil(sceneProgress.estimatedSeconds / 60)} minutes
                    </p>
                  )}
                </div>
              )}
              
              <Button 
                onClick={() => existingScenesCount > 0 ? setShowDeleteConfirm(true) : handleAutoCreateScenes(false)}
                disabled={creatingScenes || deletingScenes}
                className={`w-full gap-2 ${existingScenesCount > 0 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-indigo-500 hover:bg-indigo-600'
                } disabled:opacity-50`}
                data-testid="auto-scenes-btn"
              >
                {creatingScenes ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {sceneProgress ? `Creating Shot ${sceneProgress.current}/${sceneProgress.total}...` : 'Analyzing Script...'}
                  </>
                ) : deletingScenes ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : existingScenesCount > 0 ? (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete All Shots
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" />
                    Create Shot List
                  </>
                )}
              </Button>
              {existingScenesCount === 0 && (
                <p className="text-xs text-zinc-500 mt-2">
                  Includes auto-generated action directions for each scene (~3 sec per scene)
                </p>
              )}
            </CardContent>
          </Card>

          {/* Script Format Helper Tool - Coming Soon */}
          <Card className="bg-zinc-900/50 border-zinc-800 opacity-60">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <FileText className="w-6 h-6 text-amber-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-white text-lg">Script Format Helper</CardTitle>
                    <span className="px-2 py-0.5 text-[10px] font-medium bg-amber-500/20 text-amber-400 rounded-full">
                      Coming Soon
                    </span>
                  </div>
                  <CardDescription className="text-zinc-400">
                    Convert and format your scripts
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-zinc-500 mb-4">
                Helps format your script into the standard screenplay format that works best with the shot list generator.
              </p>
              <Button 
                disabled={true}
                className="w-full bg-amber-500/20 text-amber-400 gap-2 cursor-not-allowed"
              >
                <FileText className="w-4 h-4" />
                Coming Soon
              </Button>
            </CardContent>
          </Card>

          {/* PDF Script Generator Tool */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <FileText className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Script PDF Generator</CardTitle>
                  <CardDescription className="text-zinc-400">
                    Generate highlighted scripts for actors
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-zinc-500 mb-4">
                Generate a PDF script for a specific character with their lines highlighted. 
                Choose full script or sides (just their lines with surrounding context).
              </p>
              <Button 
                onClick={() => {
                  if (!inventory?.characters?.length) {
                    toast.info("Run Asset Inventory first to detect characters");
                    handleAnalyzeScript();
                  }
                  setShowPdfDialog(true);
                }}
                className="w-full bg-green-500 hover:bg-green-600 text-black gap-2"
                data-testid="pdf-script-btn"
              >
                <FileText className="w-4 h-4" />
                Generate Script PDF
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Delete Result */}
        {deleteResult && deleteResult.deleted_count > 0 && (
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="delete-results">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                <Trash2 className="w-5 h-5 text-red-400" />
                <div>
                  <p className="text-red-400 font-medium">
                    Deleted {deleteResult.deleted_count} scenes
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inventory Results */}
        {loadingInventory ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center gap-3 py-8">
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                <span className="text-zinc-400">Loading saved inventory...</span>
              </div>
            </CardContent>
          </Card>
        ) : inventory ? (
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="inventory-results">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Inventory Results</CardTitle>
                  {inventory.generated_at && (
                    <p className="text-xs text-zinc-500 mt-1">
                      Last generated: {new Date(inventory.generated_at).toLocaleString()}
                    </p>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleAnalyzeScript}
                  disabled={analyzing}
                  className="text-zinc-400 hover:text-white gap-1"
                >
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Refresh
                </Button>
              </div>
              
              {/* Filters */}
              <div className="mt-4 space-y-3">
                {/* Status Filter */}
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-zinc-500" />
                  <span className="text-xs text-zinc-500 mr-2">Status:</span>
                  <div className="flex gap-1">
                    {[
                      { value: "all", label: "All" },
                      { value: "missing", label: "Missing" },
                      { value: "found", label: "In Library" }
                    ].map(opt => (
                      <Button
                        key={opt.value}
                        variant="ghost"
                        size="sm"
                        onClick={() => setStatusFilter(opt.value)}
                        className={`h-7 px-2 text-xs ${
                          statusFilter === opt.value 
                            ? "bg-zinc-700 text-white" 
                            : "text-zinc-400 hover:text-white"
                        }`}
                        data-testid={`filter-status-${opt.value}`}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* Type Filter */}
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-zinc-500" />
                  <span className="text-xs text-zinc-500 mr-2">Type:</span>
                  <div className="flex gap-1">
                    {Object.entries(ASSET_TYPE_CONFIG).map(([type, config]) => {
                      const Icon = config.icon;
                      const isActive = typeFilters.includes(type);
                      return (
                        <Button
                          key={type}
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleTypeFilter(type)}
                          className={`h-7 px-2 text-xs gap-1 ${
                            isActive 
                              ? `bg-${config.color}-500/20 text-${config.color}-400` 
                              : "text-zinc-500 hover:text-white"
                          }`}
                          style={isActive ? { 
                            backgroundColor: `rgb(var(--${config.color}-500) / 0.2)`,
                          } : {}}
                          data-testid={`filter-type-${type}`}
                        >
                          <Icon className="w-3 h-3" />
                          {config.label}
                          {isActive && <CheckCircle className="w-3 h-3" />}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              {/* Scrollable inventory list */}
              <ScrollArea className="h-[350px] pr-4">
                <div className="space-y-2">
                  {filteredInventory.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">
                      No items match the current filters
                    </div>
                  ) : (
                    filteredInventory.map((item, idx) => {
                      // Use backend's exists flag (from art-inventory matching)
                      const isFound = item.exists === true;
                      const matchingAssetCount = isFound ? 1 : 0;
                      return (
                        <div 
                          key={`${item.type}-${idx}`} 
                          className="p-2 bg-zinc-800/50 rounded-lg hover:bg-zinc-800/70 transition-colors group cursor-pointer"
                          onClick={() => handleInventoryItemClick(item)}
                        >
                          <div className="flex items-center gap-2">
                            {/* Left side - icon and name (truncatable) */}
                            <div className="flex items-center gap-2 min-w-0 flex-shrink">
                              <span className={`flex-shrink-0 ${getTypeColor(item.type)}`}>
                                {getAssetIcon(item.type)}
                              </span>
                              <span className="text-white text-sm truncate max-w-[120px]" title={item.name}>{item.name}</span>
                            </div>
                            
                            {/* Middle - badges (can wrap or hide on small screens) */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500 px-1 py-0 whitespace-nowrap">
                                {ASSET_TYPE_CONFIG[item.type]?.label.slice(0, -1)}
                              </Badge>
                              {/* Speaking indicator for characters */}
                              {item.type === "character" && (
                                <Badge 
                                  variant="outline" 
                                  className={`text-[10px] px-1 py-0 gap-0.5 whitespace-nowrap ${
                                    item.has_speaking_lines
                                      ? "border-purple-500/50 text-purple-400 bg-purple-500/10" 
                                      : "border-zinc-600 text-zinc-500"
                                  }`}
                                  title={item.has_speaking_lines ? "Has speaking lines" : "Non-speaking role"}
                                >
                                  <Mic className="w-2.5 h-2.5" />
                                  {item.has_speaking_lines ? "Speaking" : "Silent"}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Spacer */}
                            <div className="flex-1" />
                            
                            {/* Right side - action buttons (always visible) */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {/* Delete button - always visible on hover */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleRemoveFromInventory(item.name, item.type); }}
                                className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`delete-${item.type}-${idx}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                              
                              {isFound ? (
                                <Badge 
                                  className="bg-green-500/20 text-green-400 border-0 gap-1 whitespace-nowrap cursor-pointer hover:bg-green-500/30"
                                  title={item.match_name ? `Matched: ${item.match_name}` : "Found in library"}
                                >
                                  <CheckCircle className="w-3 h-3" />
                                  {item.match_name ? (
                                    <span className="truncate max-w-[80px]">{item.match_name}</span>
                                  ) : (
                                    item.type === "character" ? "Profile" : "Asset"
                                  )}
                                </Badge>
                              ) : (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); handleUploadClick(item.name, item.type); }}
                                    disabled={uploading}
                                    className="h-7 px-2 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 whitespace-nowrap"
                                    data-testid={`upload-${item.type}-${idx}`}
                                  >
                                    <Upload className="w-3 h-3 mr-1" />
                                    Upload
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); handleImportClick(item.name, item.type); }}
                                    className="h-7 px-2 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 whitespace-nowrap"
                                    data-testid={`import-${item.type}-${idx}`}
                                  >
                                    <FolderInput className="w-3 h-3 mr-1" />
                                    Import
                                  </Button>
                                  <Badge className="bg-red-500/20 text-red-400 border-0 gap-1 whitespace-nowrap">
                                    <AlertCircle className="w-3 h-3" />
                                    Missing
                                  </Badge>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Context for backgrounds and props */}
                          {(item.type === "background" || item.type === "prop") && item.context && (
                            <p className="text-[11px] text-zinc-500 mt-1.5 ml-6 italic line-clamp-2">
                              &quot;{item.context}&quot;
                            </p>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>

              {/* Summary */}
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">
                    Showing {filteredInventory.length} of {totalItems} items
                  </span>
                  <span className="text-white font-medium">
                    {foundItems} in library / {totalItems - foundItems} missing
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Auto Scene Results */}
        {autoSceneResult && (
          <Card className="bg-zinc-900/50 border-zinc-800" data-testid="auto-scene-results">
            <CardHeader>
              <CardTitle className="text-white">Shots Created</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-green-400 font-medium">
                      Successfully created {autoSceneResult.scenes_created} scenes
                    </p>
                    <p className="text-green-400/70 text-sm">
                      {autoSceneResult.skipped > 0 && `${autoSceneResult.skipped} duplicates skipped. `}
                      Assets will be suggested in the scene editor.
                    </p>
                  </div>
                </div>

                <ScrollArea className="h-[300px]">
                  <div className="space-y-2 pr-4">
                    {autoSceneResult.scenes?.map((scene, idx) => (
                      <div key={idx} className="p-3 bg-zinc-800/50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium text-sm">
                            Scene {idx + 1}: {scene.name}
                          </span>
                          <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">
                            {scene.type}
                          </Badge>
                        </div>
                        <p className="text-zinc-500 text-xs line-clamp-2">
                          {scene.script_segment}
                        </p>
                        {/* Show suggested characters and location */}
                        {(scene.suggested_characters?.length > 0 || scene.suggested_location) && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {scene.suggested_location && (
                              <Badge className="text-xs bg-amber-500/20 text-amber-400 border-0">
                                📍 {scene.suggested_location}
                              </Badge>
                            )}
                            {scene.suggested_characters?.map((char, cidx) => (
                              <Badge key={cidx} className="text-xs bg-indigo-500/20 text-indigo-400 border-0">
                                👤 {char}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                <Button 
                  onClick={() => navigate(`/project/${projectId}`)}
                  className="w-full bg-zinc-700 hover:bg-zinc-600"
                  data-testid="view-scenes-btn"
                >
                  <Film className="w-4 h-4 mr-2" />
                  View All Scenes
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!project?.script?.trim() && (
          <Card className="bg-zinc-900/50 border-zinc-800 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileSearch className="w-12 h-12 text-zinc-600 mb-4" />
              <p className="text-zinc-400 text-center mb-4">
                No script found. Add a script to use these tools.
              </p>
              <Button 
                onClick={() => navigate(`/project/${projectId}`)}
                variant="outline"
                className="border-zinc-700 text-zinc-300"
              >
                Go to Script Editor
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete All Shots Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete All Shots?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              <div className="space-y-3">
                <p>
                  This will permanently delete <strong className="text-white">{existingScenesCount} shots</strong> from this project.
                </p>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-red-400 text-sm">
                    <strong>Warning:</strong> This includes any shots that have already been worked on, 
                    including assigned characters, backgrounds, and animation settings. This action cannot be undone.
                  </p>
                </div>
                <p className="text-sm">
                  After deleting, you can create a new shot list from the script.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllScenes}
              disabled={deletingScenes}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingScenes ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete All Shots
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
